import { useMemo } from 'react';

export default Gauge;

type ValueColor = [number, number, string];
interface Palette {
  background: string;
  accent: string;
  values: Array<ValueColor>;
}

const canvasSize = 100;
const center = {
  x: canvasSize / 2,
  y: canvasSize / 2,
};

function Gauge(props: Readonly<{ value: number; strokeWidth?: number }>) {
  const { value, strokeWidth = 5 } = props;
  const palette: Palette = {
    background: 'rgba(99,219,113, .2)',
    accent: '#22C3B1',
    values: [
      [0.0, 0.25, '#D12D25'],
      [0.25, 0.5, '#FFB800'],
      [0.5, 0.75, '#B2F81F'],
      [0.75, 1.0, '#1FF85C'],
    ],
  };
  const chartTransform = useMemo(() => ({ transform: 'rotate(-90deg)' }), []);
  const radius = 0.5 * (canvasSize - strokeWidth);
  const fullCircleLength = 2 * Math.PI * radius;

  const gradient = useMemo(() => {
    const result = {
      line: {
        x1: canvasSize - strokeWidth / 2,
        y1: canvasSize / 2,
        x2: canvasSize - strokeWidth / 2,
        y2: canvasSize / 2,
      },
      stops: [
        { offset: '0%', color: 'transparent' },
        { offset: '100%', color: 'transparent' },
      ],
    };

    if (value === 0) {
      return result;
    }

    if (value > 1 || value < 0) {
      console.warn('Gauge accepts values between 0 and 1. Received:', value);
      return result;
    }

    const [, , color] =
      palette.values.find(
        ([lBound, uBound]) => lBound < value && value <= uBound,
      ) || [];

    result.stops[0].color = palette.accent;
    result.stops[1].color = color ?? '';

    const angle = 2 * Math.PI * value;

    result.line.x2 = center.x + Math.cos(angle) * radius;
    result.line.y2 = center.y + Math.sin(angle) * radius;

    if (value >= 0.94) {
      // edge case
      result.line.y1 = canvasSize - canvasSize * 0.37;
      result.line.y2 = canvasSize * 0.37;

      result.stops[0].offset = '40%';
    }

    return result;
  }, [palette.accent, palette.values, radius, value]);

  return (
    <svg
      viewBox={`0 0 ${canvasSize} ${canvasSize}`}
      xmlns="http://www.w3.org/2000/svg"
      style={chartTransform}
    >
      <script>/* TODO: need to explore, may be useful */</script>
      <defs>
        <linearGradient
          id="gradient"
          {...gradient.line}
          gradientUnits="userSpaceOnUse"
        >
          {gradient.stops.map(({ offset, color }, index) => (
            <stop key={index} offset={offset} stopColor={color} />
          ))}
        </linearGradient>
      </defs>

      <circle
        cx={center.x}
        cy={center.y}
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
        stroke={palette.background}
      />
      <circle
        cx={center.x}
        cy={center.y}
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
        stroke="url(#gradient)"
        strokeDasharray={`${fullCircleLength * value} ${fullCircleLength}`}
        strokeLinecap="round"
      />
    </svg>
  );
}
